import React, { useState, useEffect } from "react";
import MetaTags from 'react-meta-tags';
import { Container } from "reactstrap";
import { useTranslation } from "react-i18next";
import PatientProfile from "./components/profile";
import Tabs from "./components/tabs";
import LiveTreatmentBanner from "./components/LiveTreatmentBanner";
import { useLocation, useHistory, Link } from "react-router-dom";
import cookieHelper from "helpers/getCookieData";
import { get } from 'helpers/api_helper';
import { PATIENT_VIEW_URL } from 'helpers/url_helper';
import { decodePatientId, encodePatientId } from 'helpers/url_crypto_helper';
import { getUserRole } from 'helpers/permissionHelper';
import { RiArrowLeftLine, RiUserHeartLine, RiHospitalLine } from "react-icons/ri";
import "./patient-chart.scss";

const PatientDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const rawToken = params.get("_p");
  const id = decodePatientId(rawToken);
  
  const role = getUserRole();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
  const [patientData, setPatientData] = useState(null);
  const [isPatinetSummary, setIsPatinetSummary] = useState(false);

  // If URL has unencoded numeric ID, replace in history with encoded token
  useEffect(() => {
    if (rawToken && /^\d+$/.test(rawToken)) {
      const encToken = encodePatientId(rawToken);
      params.set("_p", encToken);
      history.replace({
        pathname: location.pathname,
        search: `?${params.toString()}`
      });
    }
  }, [rawToken]);

  const fatchData = async (patientId) => {
    if (!patientId) return;
    try {
      const res = await get(`${PATIENT_VIEW_URL}/${patientId}`);
      if (res.success) {
        setPatientData(res.body);
      }
    } catch (e) {
      console.error("Error fetching patient details:", e);
    }
  };

  useEffect(() => {
    if (!id) return;

    const stateData = location?.state?.patientData;
    if (stateData && String(stateData.id) === String(id)) {
      setPatientData(stateData);
      localStorage.setItem("patientData", JSON.stringify(stateData));
      return;
    }

    const saved = localStorage.getItem("patientData");
    let storedData = null;

    try {
      storedData = saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.warn("Invalid patientData in localStorage", e);
    }

    // If localStorage matches the current ID → use it
    if (storedData && String(storedData.id) === String(id)) {
      setPatientData(storedData);
    } else {
      // Otherwise hit the API
      fatchData(id);
    }
  }, [location?.state?.patientData, id]);

  const authUserCookie = cookieHelper.getCookie('authUser');
  let configData = null;
  try {
    configData = authUserCookie ? JSON.parse(authUserCookie) : null;
  } catch (e) {
    configData = null;
  }

  if (!patientData) {
    return (
      <div className="page-content text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Patient Details...</span>
        </div>
        <p className="text-muted font-size-13 mt-2">Loading Clinical EMR Chart...</p>
      </div>
    );
  }

  const patientFullName = `${patientData?.title || 'Mr.'} ${patientData?.first_name || ''} ${patientData?.last_name || ''}`.trim();

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{patientFullName} | Clinical EMR Chart | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>
        <Container fluid>
          {/* Breadcrumb Navigation Bar */}
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <Link 
                to="/patients" 
                className="btn btn-sm btn-light border text-muted d-inline-flex align-items-center gap-1 font-weight-medium"
                title={t("Back to Directory") || "Back to All Patients"}
              >
                <RiArrowLeftLine /> {t("Back to Directory") || "Back to Directory"}
              </Link>
              <span className="text-muted font-size-13">/</span>
              <span className="text-slate-800 font-weight-semibold font-size-13 d-flex align-items-center gap-1">
                <RiUserHeartLine className="text-primary" /> {patientFullName}
              </span>
              <span className="badge bg-light text-primary border font-size-11">
                {patientData?.case_no || `#${patientData?.id}`}
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-light text-dark border font-size-12 px-2.5 py-1.5 d-flex align-items-center gap-1">
                <RiHospitalLine className="text-primary" /> {clinic?.name || 'Oralstop Home'}
              </span>
            </div>
          </div>

          {/* Live Dental Chair Session Controller Banner (Active when patient is checked in or in treatment) */}
          <LiveTreatmentBanner 
            patientData={patientData} 
            apptId={params.get('_appt')} 
            onSessionUpdated={() => fatchData(id)} 
          />

          {/* Clinical Profile Header Bar */}
          <PatientProfile 
            data={patientData} 
            configData={configData} 
            callback={setIsPatinetSummary} 
          />

          {/* EMR Tabs Ribbon & Active Component */}
          <Tabs 
            patientData={patientData} 
            configData={configData} 
            role={role} 
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PatientDetails;
